/**
 * colorConverters — pure kleur-conversie-helpers, gedeeld door de Color-converter
 * pagina en de dashboard-widget. Source of truth = RGB triplet {r,g,b} (0-255).
 */

export const clampColor = (v, min, max) => Math.max(min, Math.min(max, v));
const round = (v, p = 0) => Number(v.toFixed(p));

export function rgbToHex({ r, g, b }) {
  const h = (n) => clampColor(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}

export function hexToRgb(input) {
  if (!input) return null;
  let v = String(input).trim().replace(/^#/, '');
  if (v.length === 3) v = v.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(v)) return null;
  return {
    r: parseInt(v.slice(0, 2), 16),
    g: parseInt(v.slice(2, 4), 16),
    b: parseInt(v.slice(4, 6), 16),
  };
}

export function rgbToHsl({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case R: h = (G - B) / d + (G < B ? 6 : 0); break;
      case G: h = (B - R) / d + 2; break;
      default: h = (R - G) / d + 4;
    }
    h *= 60;
  }
  return { h: round(h, 0), s: round(s * 100, 0), l: round(l * 100, 0) };
}

export function hslToRgb({ h, s, l }) {
  const H = ((h % 360) + 360) % 360 / 360;
  const S = clampColor(s, 0, 100) / 100;
  const L = clampColor(l, 0, 100) / 100;
  if (S === 0) { const v = Math.round(L * 255); return { r: v, g: v, b: v }; }
  const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
  const p = 2 * L - q;
  const hue = (t) => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return {
    r: Math.round(hue(H + 1 / 3) * 255),
    g: Math.round(hue(H) * 255),
    b: Math.round(hue(H - 1 / 3) * 255),
  };
}

export function rgbToHsv({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const v = max;
  const d = max - min;
  const s = max === 0 ? 0 : d / max;
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case R: h = (G - B) / d + (G < B ? 6 : 0); break;
      case G: h = (B - R) / d + 2; break;
      default: h = (R - G) / d + 4;
    }
    h *= 60;
  }
  return { h: round(h, 0), s: round(s * 100, 0), v: round(v * 100, 0) };
}

export function hsvToRgb({ h, s, v }) {
  const H = ((h % 360) + 360) % 360 / 60;
  const S = clampColor(s, 0, 100) / 100;
  const V = clampColor(v, 0, 100) / 100;
  const i = Math.floor(H);
  const f = H - i;
  const p = V * (1 - S);
  const q = V * (1 - f * S);
  const t = V * (1 - (1 - f) * S);
  let R = 0, G = 0, B = 0;
  switch (i % 6) {
    case 0: R = V; G = t; B = p; break;
    case 1: R = q; G = V; B = p; break;
    case 2: R = p; G = V; B = t; break;
    case 3: R = p; G = q; B = V; break;
    case 4: R = t; G = p; B = V; break;
    default: R = V; G = p; B = q;
  }
  return { r: Math.round(R * 255), g: Math.round(G * 255), b: Math.round(B * 255) };
}

export function rgbToCmyk({ r, g, b }) {
  const R = r / 255, G = g / 255, B = b / 255;
  const k = 1 - Math.max(R, G, B);
  if (k === 1) return { c: 0, m: 0, y: 0, k: 100 };
  return {
    c: round(((1 - R - k) / (1 - k)) * 100, 0),
    m: round(((1 - G - k) / (1 - k)) * 100, 0),
    y: round(((1 - B - k) / (1 - k)) * 100, 0),
    k: round(k * 100, 0),
  };
}

export function cmykToRgb({ c, m, y, k }) {
  const C = clampColor(c, 0, 100) / 100;
  const M = clampColor(m, 0, 100) / 100;
  const Y = clampColor(y, 0, 100) / 100;
  const K = clampColor(k, 0, 100) / 100;
  return {
    r: Math.round(255 * (1 - C) * (1 - K)),
    g: Math.round(255 * (1 - M) * (1 - K)),
    b: Math.round(255 * (1 - Y) * (1 - K)),
  };
}

export function relLuminance({ r, g, b }) {
  const c = (n) => {
    const v = n / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
}

export function contrastRatio(L1, L2) {
  const lighter = Math.max(L1, L2), darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

export const randomRgb = () => ({
  r: Math.floor(Math.random() * 256),
  g: Math.floor(Math.random() * 256),
  b: Math.floor(Math.random() * 256),
});

// Handige formatter-strings
export const fmt = {
  rgb: ({ r, g, b }) => `rgb(${r}, ${g}, ${b})`,
  hsl: ({ h, s, l }) => `hsl(${h}, ${s}%, ${l}%)`,
  hsv: ({ h, s, v }) => `hsv(${h}, ${s}%, ${v}%)`,
  cmyk: ({ c, m, y, k }) => `cmyk(${c}%, ${m}%, ${y}%, ${k}%)`,
};
